import { motion } from 'framer-motion';
import { 
  FileText, 
  Scale, 
  Shield, 
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Download
} from 'lucide-react';
import { Link } from 'react-router-dom';

const NormativePage = () => {
  const regulations = [
    {
      code: 'D.Lgs. 81/08',
      title: 'Testo Unico Sicurezza sul Lavoro',
      description: 'Il decreto legislativo 81/2008 è la normativa di riferimento per la salute e sicurezza nei luoghi di lavoro. Definisce gli obblighi del datore di lavoro in materia di prevenzione delle cadute dall\'alto.',
      key_points: [
        'Obbligo di valutazione dei rischi',
        'Misure di protezione collettive prioritarie',
        'Formazione obbligatoria per i lavoratori',
        'Documentazione e registro delle manutenzioni'
      ],
      link: 'https://www.gazzettaufficiale.it/eli/id/2008/04/30/008G0104/sg'
    },
    {
      code: 'UNI EN 795',
      title: 'Dispositivi di Ancoraggio',
      description: 'La norma UNI EN 795 definisce i requisiti per i dispositivi di ancoraggio destinati all\'uso con dispositivi di protezione individuale contro le cadute. Classifica i dispositivi in diverse tipologie (A, B, C, D, E).',
      key_points: [
        'Classificazione dispositivi (Tipo A, B, C, D, E)',
        'Requisiti di resistenza e prestazione',
        'Istruzioni per installazione e uso',
        'Marcatura e documentazione'
      ],
      link: 'https://store.uni.com/en/uni-en-795-2012'
    },
    {
      code: 'UNI 11560',
      title: 'Sistemi di Ancoraggio Permanenti',
      description: 'La norma UNI 11560 specifica i requisiti per la progettazione, installazione, uso e manutenzione dei sistemi di ancoraggio permanenti utilizzati come punti di ancoraggio per dispositivi di protezione individuale contro le cadute.',
      key_points: [
        'Criteri di progettazione del sistema',
        'Requisiti per l\'installazione',
        'Piano di manutenzione periodica',
        'Competenze del personale'
      ],
      link: 'https://store.uni.com/en/uni-11560-2022'
    },
    {
      code: 'UNI 11900',
      title: 'Installatore Linee Vita',
      description: 'La norma UNI 11900:2023 definisce i requisiti di conoscenza, abilità e competenza dell\'installatore di sistemi di ancoraggio permanenti in copertura. Fondamentale per garantire installazioni sicure e conformi.',
      key_points: [
        'Requisiti formativi dell\'installatore',
        'Competenze tecniche richieste',
        'Aggiornamento professionale',
        'Certificazione delle competenze'
      ],
      link: 'https://store.uni.com/en/uni-11900-2023'
    },
    {
      code: 'UNI EN 13374',
      title: 'Parapetti Provvisori',
      description: 'Definisce i requisiti per i sistemi temporanei di protezione dei bordi utilizzati durante la costruzione o la manutenzione di edifici e altre strutture. Classifica i parapetti in tre classi (A, B, C).',
      key_points: [
        'Classificazione in base all\'inclinazione',
        'Requisiti di resistenza statica e dinamica',
        'Altezze e dimensioni minime',
        'Prove di tipo'
      ],
      link: 'https://store.uni.com/en/uni-en-13374-2019'
    }
  ];

  const regionalRegulations = [
    { region: 'Lombardia', law: 'L.R. 7/2016', description: 'Obbligo linee vita su nuove costruzioni e ristrutturazioni' },
    { region: 'Veneto', law: 'D.G.R. 2774/2009', description: 'Disposizioni in materia di sicurezza per l\'accesso in copertura' },
    { region: 'Emilia-Romagna', law: 'D.G.R. 149/2016', description: 'Requisiti minimi per la sicurezza in copertura' },
    { region: 'Piemonte', law: 'D.G.R. 64-13268', description: 'Linee guida per la sicurezza sui tetti' },
    { region: 'Toscana', law: 'D.P.G.R. 75/R', description: 'Disposizioni sui dispositivi permanenti anticaduta' }
  ];

  return (
    <div className="bg-revo-bg pt-20 min-h-screen">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              Riferimenti Normativi
            </span>
            <h1 className="font-barlow text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mt-4 mb-6">
              Normative<br />
              <span className="text-gray-500">& Certificazioni</span>
            </h1>
            <p className="font-manrope text-lg text-gray-300 leading-relaxed">
              Le normative di riferimento per i sistemi anticaduta e le linee vita. 
              Tutti i nostri progetti sono conformi alle leggi vigenti.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Regulations */}
      <section className="py-16 bg-revo-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12">
            <h2 className="font-barlow text-3xl font-bold uppercase tracking-tight text-white">
              Normative Nazionali
            </h2>
            <p className="font-manrope text-gray-400 mt-2">
              Le principali norme tecniche e legislative di riferimento
            </p>
          </div>

          <div className="space-y-6" data-testid="regulations-list">
            {regulations.map((reg, index) => (
              <motion.div
                key={reg.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-revo-bg border border-revo-border p-8 hover:border-revo-orange/30 transition-colors"
                data-testid={`regulation-${reg.code.replace(/\s/g, '-').toLowerCase()}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  {/* Code Badge */}
                  <div className="lg:w-48 flex-shrink-0">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-revo-orange/10 border border-revo-orange/20">
                      <FileText className="w-4 h-4 text-revo-orange" />
                      <span className="font-barlow font-bold text-revo-orange uppercase">
                        {reg.code}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-barlow text-xl font-bold uppercase tracking-wide text-white mb-3">
                      {reg.title}
                    </h3>
                    <p className="font-manrope text-gray-400 leading-relaxed mb-6">
                      {reg.description}
                    </p>

                    {/* Key Points */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                      {reg.key_points.map((point, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-revo-orange flex-shrink-0" />
                          <span className="font-manrope text-sm text-gray-300">{point}</span>
                        </div>
                      ))}
                    </div>

                    {/* Link */}
                    <a
                      href={reg.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-revo-orange hover:underline font-manrope text-sm"
                    >
                      <span>Consulta la norma</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional Regulations */}
      <section className="py-16 bg-revo-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12">
            <h2 className="font-barlow text-3xl font-bold uppercase tracking-tight text-white">
              Normative Regionali
            </h2>
            <p className="font-manrope text-gray-400 mt-2">
              Disposizioni specifiche per regione in materia di linee vita
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="regional-regulations">
            {regionalRegulations.map((reg, index) => (
              <motion.div
                key={reg.region}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-revo-paper border border-revo-border p-6"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Scale className="w-5 h-5 text-revo-orange" />
                  <span className="font-barlow font-bold text-white uppercase">
                    {reg.region}
                  </span>
                </div>
                <div className="font-manrope text-sm text-revo-orange mb-2">
                  {reg.law}
                </div>
                <p className="font-manrope text-sm text-gray-400">
                  {reg.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-16 bg-revo-paper">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-yellow-500 flex-shrink-0" />
              <div>
                <h3 className="font-barlow text-xl font-bold uppercase text-yellow-500 mb-3">
                  Responsabilità del Proprietario
                </h3>
                <p className="font-manrope text-gray-300 leading-relaxed mb-4">
                  Il proprietario dell'immobile è responsabile della corretta installazione e manutenzione 
                  dei sistemi anticaduta. L'omessa installazione o la mancata revisione periodica possono 
                  comportare sanzioni amministrative e penali in caso di infortunio.
                </p>
                <p className="font-manrope text-sm text-gray-400">
                  È fondamentale affidarsi a professionisti qualificati e mantenere aggiornata 
                  la documentazione tecnica del sistema.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-revo-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-barlow text-3xl font-bold uppercase tracking-tight text-white mb-4">
            Hai Bisogno di Assistenza?
          </h2>
          <p className="font-manrope text-gray-400 mb-8">
            I nostri tecnici sono a disposizione per chiarire ogni dubbio sulle normative 
            e verificare la conformità dei tuoi sistemi anticaduta.
          </p>
          <Link
            to="/contatti"
            className="inline-flex items-center gap-2 bg-revo-orange text-white px-8 py-4 font-barlow font-bold uppercase tracking-widest hover:bg-revo-orange-dark transition-all"
            data-testid="normative-cta"
          >
            Richiedi Consulenza
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NormativePage;
